import { authFetchGET, authFetchPOST, authFetchPUT } from '../utils/auth-fetch';
import { VoyagePlan, PlanStatus, PassagePlanDraft, PassagePlanSquat, PassagePlanUKC,
         PassagePlanBridgeTeam, 
         VesselCharacteristics,
         Signature,
         RouteAdoptionResult,
         RouteAdoptionPortPair,
         VesselPlanningModel} from '../models/vessel-planning-model';
import { BprAPIResponse } from '../models/types/voyage-best-practice-routes-impl.types';
import { UUID, isOnline } from '../utils/helpers';
import { VoyageRoute } from '../models/voyage-routing-model';
import { VoyageImoModel } from "../models/voyage-imo-model";
import { IndexedDBService } from "../../indexed-db/services/db_service";
import { VoyageDialogPopupManager } from '../../local_modules/voyage-dialog-popup/src/voyage-dialog-popup-manager';
import { AddWeatherRoutingInfoObject } from '../../local_modules/voyage-suggested-routes/src/helper';
import { IntermediatePlan } from '../models/schedule-change-model';
import { ISWEATHERROUTINGENABLED } from '../models/feature-flag-model-impl';

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;
const vesselIMONumber = (<any>window)["appenv"].vesselIMONumber;
const vesselplansApi = (<any>window)["appenv"].services.vesselplansapi;
const env = { ...vesselplansApi, ...isVesselOnCloud ? { scope: (<any>window)["appenv"].scope } : {} };

/** Cloud create/update use /v2/plans (slim response); other plan APIs stay on /v1. */
function plansWriteBaseurl(): string {
    if (!isVesselOnCloud) {
        return env.baseurl;
    }
    return String(env.baseurl || '').replace(/\/v1\/?$/, '/v2');
}


interface PlansApiPlan
{
    id: number;
    imoNo: number;
    scheduleId: string;
    create_dtm: string;
    etd: string;
    eta: string;
    departureTerminalCode: string;
    startPosition: [number, number]|null;
    departurePilotPosition: [number, number]|null;
    departurePilotEtd: string;
    arrivalTerminalCode: string;
    endPosition: [number, number]|null;
    arrivalPilotPosition: [number, number]|null;
    arrivalPilotEta: string;
    foreDraft: number;
    aftDraft: number;
    gmt: number|null;
    solidGm?: number|null;
    numberOfReefers: number;
    status: PlanStatus;
    passagePlan: {
        description: string;
        departurePort: string;
        arrivalPort: string;
        portMemo: string;
        reminders: string;
        encCellsUsed: string;
        booksAndTables: string;
        chartsAndLightsNotes: string;
        sailingDirections: string;
        listOfRadioSignals: string;
        listOfLights: string;
        gpsEcdisRadarMap: string;
        pubCorrectedToWeek: string;
        tideTables: string;
        publicationsNotes: string;
        positionValidation: string;
        drafts: {
            berthToPilot: PassagePlanDraft;
            pilotToPilot: PassagePlanDraft;
            pilotToBerth: PassagePlanDraft;
        };
        squats: {
            minSpeed: PassagePlanSquat;
            maxSpeed: PassagePlanSquat;
            bufferSpeed: PassagePlanSquat;
            vesselMaxSpeed: PassagePlanSquat;
        };
        underKeelClearance: {
            berthToPilot: PassagePlanUKC;
            pilotToPilot: PassagePlanUKC;
            pilotToBerth: PassagePlanUKC;
        };
        safetyMessages: {
            navareaRegions: string;
            navtexStations: string;
        };
        weatherInfo: {
            navareaRegions: string;
            navtexStations: string;
            info: string;
        };
        equipmentStatus: {
            operationalCondition: string;
            failures: string;
        };
        bridgeTeam: {
            departure: PassagePlanBridgeTeam;
            arrival: PassagePlanBridgeTeam;
        };
        calculatedValues: {
            avgSpeedOGPilotToPilot: number|null;
            routeMinSpeed: number|null;
            routeMaxSpeed: number|null;
            routeAverageSpeed: number|null;
            berthToPilotDistance: number|null;
            pilotToPilotDistance: number|null;
            pilotToBerthDistance: number|null;
        };
        signatures: Signature[]
    };
    routes: any[];
    selectedRouteIndex: number;
}

export interface PlanWithId extends VoyagePlan
{
    id: number;
}

export interface CharacteristicsWithIMO extends VesselCharacteristics
{
    imoNo: number;
}

export class VesselPlansService
{
    private static _extractCreatePlanErrorMessage(err: any): string
    {
        const candidates: Array<unknown> = [
            err?.response?.data?.message,
            err?.response?.data?.error,
            err?.response?.data?.detail,
            err?.response?.data?.title,
            err?.message,
            err?.error,
        ];

        const firstString = candidates.find(value => typeof value === 'string' && value.trim().length > 0) as string | undefined;
        if (firstString)
        {
            return firstString;
        }

        if (Array.isArray(err?.response?.data?.errors))
        {
            const messages = err.response.data.errors
                .map((item: any) => (typeof item?.message === 'string' ? item.message : (typeof item === 'string' ? item : '')))
                .filter((item: string) => item.length > 0);
            if (messages.length)
            {
                return messages.join('; ');
            }
        }

        return "Failed to create plan";
    }

    static async getAllPlans(): Promise<PlanWithId[]>
    {
        let plans = [];
        try {
            // if offline get plans data from IndexedDB
            if ((isVesselOnCloud && !isOnline())) {
                plans = await IndexedDBService.plans.getAll();
            }
            plans = await authFetchGET(env.scope, `${env.baseurl}/plans`);
            // Sort based on create_dtm so newer plans are first in the array
            plans.sort( (a: PlansApiPlan, b: PlansApiPlan) => {
                new Date(b.create_dtm).getTime() - new Date(a.create_dtm).getTime();
            });

            plans = plans.map( (plan: PlansApiPlan) => this._convertFromPlansApi(plan) );
        } catch (err) {
            console.log('Failed to fetch plans', err);
        }
        return plans;
    }

    static async getPlansByScheduleId(scheduleIds: string[]): Promise<PlanWithId[]>
    {
        let plans = [];
        try {
            // if offline get plans data from IndexedDB
            if  (isVesselOnCloud && !isOnline()) {
                plans = await IndexedDBService.plans.getByScheduleIds(scheduleIds);
                return plans as PlanWithId[];
            }

            if(isVesselOnCloud) {
                // fetch the plans info
                const plansInfos: { [key: string]: boolean } | null = await this.getplanInfosByScheduleIds(scheduleIds);
                let newScheduleIds = scheduleIds.filter((scheduleId) => plansInfos && plansInfos[scheduleId] === true);
                if (newScheduleIds.length === 0) {
                    newScheduleIds = scheduleIds;
                }
                // // to fix the 10MB response size limit, we will fetch plans by scheduleIds in chunks of 2
                for (let i = 0; i < newScheduleIds.length; i += 2) {
                    if (newScheduleIds[i + 1] !== undefined) {
                        const result = await authFetchGET(env.scope, `${env.baseurl}/plans`, { scheduleId: [newScheduleIds[i], newScheduleIds[i + 1]].join(',') }, undefined, true);
                        await this.checkForPlanSizeExceedance(result);
                        
                        const plansData = result.data;
                        if (plansData) {
                            plans.push(...plansData);
                        }
                    } else {
                        // if there is an odd number of scheduleIds, fetch the last one separately
                        const result = await authFetchGET(env.scope, `${env.baseurl}/plans`, { scheduleId: newScheduleIds[i] }, undefined, true);
                        await this.checkForPlanSizeExceedance(result);
                        
                        const plansData = result.data;
                        if (plansData) {
                            plans.push(...plansData);
                        }
                    }
                }
            } else {
                plans = await authFetchGET(env.scope, `${env.baseurl}/plans`, { scheduleId: scheduleIds.join() }) || [];
            }
        
            // Sort based on create_dtm so newer plans are first in the array
            plans.sort( (a: PlansApiPlan, b: PlansApiPlan) => {
                return new Date(b.create_dtm).getTime() - new Date(a.create_dtm).getTime();
            });

            plans = plans.map( (plan: PlansApiPlan) => this._convertFromPlansApi(plan) );
            return plans;

        } catch (err) {
            console.error('Failed to fetch plans', err);
            return plans;  
        }
    }
    static async checkForPlanSizeExceedance(result: any) {
        if (result && result.headers && (result.headers['x-routes-filtered'] === "true")) {
            const dialog = VoyageDialogPopupManager.createDialog({
                headerText: "Plan Size Exceeded",
                bodyText: `The size of the current plan exceeds the limit of 10 MB. 
                You will be seeing only approved route if the plan is approved or selected route in the plan if the plan is not approved`,
                acceptButtonText: "OK"
            });
            await dialog.getResponse();
        }
    }

    static async getPlanById(id: number): Promise<PlanWithId|null>
    {
        let plan: PlanWithId | null = null;
        try
        {
            if(isVesselOnCloud && !isOnline()) {
                // if offline get plans data from IndexedDB
                plan = await IndexedDBService.plans.get(id) as PlanWithId;
                return plan;
            }
            plan = this._convertFromPlansApi(await authFetchGET(env.scope, `${env.baseurl}/plans/${id}`));
            return plan;
        } catch (err) { 
            // 404 error means there is no plan with that id - return null in this case
            console.log('Failed to fetch plan', err);
        }
        return plan;
    }

    static async updatePlan(plan: VoyagePlan, id: number, sendToShore=true): Promise<void>
    {
        // loop through plan and add routeid, weatherroutingInfo to each route
        plan?.routes?.forEach(route => {
            if(route && !route.routeId) {
                route.routeId = UUID();
            }

            const WeatherRoutingOn = localStorage.getItem(ISWEATHERROUTINGENABLED) === "true";

            if(WeatherRoutingOn && !route?.weatherRoutingInfo) {
                AddWeatherRoutingInfoObject(route);
            }
        });

        // setting source of update as Frontend to differentiate from other sources of plan updates
        plan.source = "Frontend";
        
        // setting source of update as Frontend to differentiate from other sources of plan updates
        plan.source = "Frontend";

        if(isVesselOnCloud) {
            // adding the  plan to indexed DB
            IndexedDBService.plans.add(plan);
            // find the size of plan 
            // if size of plan is greater than stargate limit of 10 mb
            // ask user to remove some routes before recalculation
            if (plan) {
                const planSize = await VesselPlanningModel!.getPlanSizeInMB(plan);
                console.log(`Plan size is ${planSize} MB : update plan` );
                const stargateLimitInMB = 10;
                if(planSize > stargateLimitInMB){
                    const dialog = VoyageDialogPopupManager.createDialog({
                        headerText: "Plan Size Exceeded",
                        bodyText: `The size of the current plan is ${planSize.toFixed(2)} MB which exceeds the limit of ${stargateLimitInMB} MB for recalculation.
                        Please remove some routes from the plan before attempting to recalculate.`,
                        acceptButtonText: "OK"
                    });
                    await dialog.getResponse();
                    return;
                }
            }
            console.log("Sending plan to shore", plan.id, sendToShore);
            // dispatch event to indicate put call to update plan is initiated
            VoyageImoModel?.dispatchEvent(new Event('updating-plan-event'));
            console.log("Plan PUT payload :", this._convertToPlansApi(plan));
            const result = await authFetchPUT(env.scope, `${plansWriteBaseurl()}/plans/${id}?sendPlanToShore=${sendToShore}`, Object.assign({id}, this._convertToPlansApi(plan)));
            if (result && !result.hasOwnProperty('error')) {
                // dispatch event to indicate put call to update plan is completed
                VoyageImoModel?.dispatchEvent(new Event('updating-plan-event-completed'));
                return result;
            } else  if(result.hasOwnProperty('error') && result && result.response && result.response.status !== 200) {
                VoyageImoModel?.dispatchEvent(new Event('updating-plan-event-failed'));
            }
        } else {
            await authFetchPUT(env.scope, `${plansWriteBaseurl()}/plans/${id}?sendPlanToShore=${sendToShore}`, Object.assign({id}, this._convertToPlansApi(plan)));
        }
    }

    static async createPlan(plan: VoyagePlan): Promise<number>
    {
        // loop through plan and add routeid to each route
         plan?.routes?.forEach(route => {
            if(route && !route.routeId) {
               route.routeId = UUID();
            }

            const WeatherRoutingOn = localStorage.getItem(ISWEATHERROUTINGENABLED) === "true";

            if(WeatherRoutingOn && !route?.weatherRoutingInfo) {
                AddWeatherRoutingInfoObject(route);
            }
        });

        // setting source of update as Frontend to differentiate from other sources of plan updates
        plan.source = "Frontend";

        IndexedDBService.plans.add(plan);
        try {
            const result = await authFetchPOST(env.scope, `${plansWriteBaseurl()}/plans`, this._convertToPlansApi(plan));
            if (typeof result?.id === "number" && result.id >= 0)
            {
                return result.id;
            }

            throw new Error(this._extractCreatePlanErrorMessage(result));
        } catch(err) {
            console.log('error while creating plan :', err);
            throw new Error(this._extractCreatePlanErrorMessage(err));
        }
    }

    static async calculatePlan(plan: VoyagePlan): Promise<PlanWithId>
    {
        //loop through plan and add routeid to each route
        plan?.routes.forEach(route => {
            if(!route.routeId) {
               route.routeId = UUID();
             }
        });
        const result = await authFetchPOST(env.scope, `${env.baseurl}/plans/calculate`, this._convertToPlansApi(plan));
        return this._convertFromPlansApi(result);
    }

    static async getVesselCharacteristics(): Promise<VesselCharacteristics|null>
    {
        let result: CharacteristicsWithIMO|null = null;

        try
        {

            // if offline get characteristics data from IndexedDB
            if(isVesselOnCloud && !isOnline()) {
               const res = await IndexedDBService.vesselCharacteristics.get()

               if(res) {
                    result = res;
               }

            } else {
                const apiUrl = `${env.baseurl}/vessel/${isVesselOnCloud ? VoyageImoModel?.imo! : vesselIMONumber}`;
                result = await authFetchGET(env.scope, apiUrl);

                if(result) {
                    IndexedDBService.vesselCharacteristics.add(result);
                }
            }

        }
        catch (e)
        {
            // 404 means nothing is set yet, return null in this case
        }

        if (!result)
        {
            return null;
        }

        return {
            height: result.height,
            minSpeed: result.minSpeed,
            maxSpeed: result.maxSpeed,
            blockCoefficient: result.blockCoefficient,
            beamSize: result.beamSize,
            ecdisWPStartIndex: result.ecdisWPStartIndex,
            legSpeedSetting: result.legSpeedSetting
        };
    }

    static async setVesselCharacteristics(characteristics: VesselCharacteristics): Promise<void>
    {
        const IMONumber = isVesselOnCloud ? VoyageImoModel?.imo! : Number(vesselIMONumber); 
        const newData: CharacteristicsWithIMO = {
            imoNo: IMONumber,
            ...characteristics
        };
        await authFetchPUT(env.scope, `${env.baseurl}/vessel/${IMONumber}`, newData);
    }

    private static _convertToPlansApi(plan: VoyagePlan): PlansApiPlan
    {
        // timestamps (ETDs/ETAs) has to be a valid timestring or omitted from the json
        const converted = JSON.parse(JSON.stringify(plan));
        converted.etd = converted.etd === -1 ? undefined : new Date(converted.etd).toISOString();
        converted.eta = converted.eta === -1 ? undefined : new Date(converted.eta).toISOString();
        converted.departurePilotEtd = converted.departurePilotEtd === -1 ? null : new Date(converted.departurePilotEtd).toISOString();
        converted.arrivalPilotEta = converted.arrivalPilotEta === -1 ? null : new Date(converted.arrivalPilotEta).toISOString();

        // Add IMO number
        converted.imoNo = isVesselOnCloud ? VoyageImoModel?.imo! : Number(vesselIMONumber);
        return converted;
    }

    private static _convertFromPlansApi(plan: PlansApiPlan): PlanWithId
    {
        const nullTime = "0001-01-01T00:00:00Z"; // Go's "zero" time - means not defined yet
        const converted = JSON.parse(JSON.stringify(plan));
        // Convert ETAs/ETDs
        converted.etd = plan.etd === nullTime ? -1 : new Date(plan.etd).getTime();
        converted.eta = plan.eta === nullTime ? -1 : new Date(plan.eta).getTime();
        converted.departurePilotEtd = plan.departurePilotEtd === null ? -1 : new Date(plan.departurePilotEtd).getTime();
        converted.arrivalPilotEta = plan.arrivalPilotEta === null ? -1 : new Date(plan.arrivalPilotEta).getTime();

        // Make sure gmt is defined
        if (converted.gmt === undefined)
        {
            converted.gmt = null;
        }

        //support signatures for old plans
        if (!converted.passagePlan.signatures || converted.passagePlan.signatures!.length === 0) {
            converted.passagePlan.signatures = [
                { rank: "", name: ""},
                { rank: "", name: ""},
                { rank: "", name: ""},
                { rank: "", name: ""},
                { rank: "", name: ""},
                { rank: "", name: ""},
            ]
        }

        return converted;
    }

    static async getOptimizedBPRsByPortPair(departureTerminalCode: string, arrivalTerminalCode: string, scheduleId: string): Promise<BprAPIResponse[]|null>{
        try {
            //if cloud vessel is offline fetch from indexed-db for the respective scheduleId
            if(isVesselOnCloud && !isOnline() && scheduleId){
                const res = await IndexedDBService.bprs.get(scheduleId);
                return res?.data ?? null;
            }
            const res = await authFetchGET(env.scope, `${env.baseurl}/bpr/optimized/portPair?departureTerminalCode=${departureTerminalCode}&arrivalTerminalCode=${arrivalTerminalCode}&scheduleId=${scheduleId}`);
            
            // If res is null, status is likely 204 due to no BPRs found and authGet has returned null. In this case, return empty array
            if (!res) {
                return null;
            }
    
            // Assuming the response is ok and has content, return it
            return res;
        } catch (e) {
            console.error("Error fetching BPRs:", e);
            return null;
        }
    }
    
    // getSailableBPRIds fetches up to `limit` (default 3) most fuel-efficient precomputed BPR IDs
    // that are sailable for the given draft, so they can be fed into a BPR ranking job. Returns an
    // empty array when none are sailable (backend responds 204) or on error.
    static async getSailableBPRIds(scheduleId: string, foreDraft: number, aftDraft: number, limit: number = 3): Promise<string[]>{
        try {
            const query = `scheduleId=${encodeURIComponent(scheduleId)}&foreDraft=${foreDraft}&aftDraft=${aftDraft}&limit=${limit}`;
            const res = await authFetchGET(env.scope, `${env.baseurl}/bpr/optimized/sailable-ids?${query}`);

            // A null response is a 204 (no sailable route for this draft).
            if (!res) {
                return [];
            }
            return (res.bprIds as string[]) ?? [];
        } catch (e) {
            console.error("Error fetching sailable BPR ids:", e);
            return [];
        }
    }
    
    static async getOptimizedOORsByPortPair(departureTerminalCode: string, arrivalTerminalCode: string, scheduleId: string): Promise<BprAPIResponse[]|null>{
        try {
            if(isVesselOnCloud && !isOnline() && scheduleId){
                const res = await IndexedDBService.oors.get(scheduleId);
                return res?.data ?? null;
            }
            const res = await authFetchGET(env.scope, `${env.baseurl}/oor/optimized/portPair?departureTerminalCode=${departureTerminalCode}&arrivalTerminalCode=${arrivalTerminalCode}&scheduleId=${scheduleId}`);
            if (!res) {
                return null;
            }
            return res;
        } catch (e) {
            console.error("Error fetching OORs:", e);
            return null;
        }
    }

    static async getWrAdvice(departureTerminalCode:string, ArrivalTerminalCode:string)
    {
        let response = await authFetchGET(env.scope, `${env.baseurl}/wrdvise?from=${departureTerminalCode}&to=${ArrivalTerminalCode}`);

        return response;
    }

    
    static async isRouteAlignedWithStarRoute(route: VoyageRoute,  bprID: string): Promise<boolean>{
        return (await this.getRouteAdoption(route, bprID)).adopted;
    }

    // Calls POST /bpr/adopted. Pass bprID to compare against a chosen star route, or portPair to let
    // the backend resolve the top-1 star route (e.g. route upload). Returns the deviation-annotated
    // route so the caller can persist the per-waypoint verdict and render exceeded waypoints.
    static async getRouteAdoption(route: VoyageRoute, bprID?: string, portPair?: RouteAdoptionPortPair, bprRoute?: VoyageRoute): Promise<RouteAdoptionResult>{
        try {
            const payload: any = {
                route: route,
                bprID: bprID ?? ''
            }
            if (bprRoute) {
                payload.bprRoute = bprRoute;
            }
            if (portPair) {
                payload.departureTerminalCode = portPair.departureTerminalCode;
                payload.arrivalTerminalCode = portPair.arrivalTerminalCode;
                payload.scheduleId = portPair.scheduleId;
            }

            const res = await authFetchPOST(env.scope, `${env.baseurl}/bpr/adopted`, payload);
            if (res) {
                return { adopted: !!res.adopted, bprId: res.bprId ?? bprID ?? '', route: res.route ?? null };
            }
        } catch (e) {
            console.error("Error checking route adoption:", e);
        }
        return { adopted: false, bprId: bprID ?? '', route: null };
    }

    static async getNewPlanWithUpdatedEta() : Promise<IntermediatePlan|null> {
        let url = `${env.baseurl}/intermediate/plan`;
         if (isVesselOnCloud && VoyageImoModel?.imo) {
            url += `?imo=${VoyageImoModel.imo}`;
        }

        try {
            let response = await authFetchGET(env.scope, url);
            const plan = this._convertFromPlansApi(response?.optimized_plan);
            response = {
                ...response,
                optimized_plan: plan
            }
            return response
        } catch (error) {
            console.error("Error fetching new plan with updated ETA ", error);
            return null;
        }
    }

    static async updatePlanWithUpdatedEta(plan: VoyagePlan): Promise<VoyagePlan | null> {
        let url = `${env.baseurl}/intermediate/plan`;
        if (isVesselOnCloud && VoyageImoModel?.imo) {
            url += `?imo=${VoyageImoModel.imo}`;
        }

        try {
            const payload = {
                plan_id : plan.id,
                optimized_plan: this._convertToPlansApi(plan)
            }
            const response = await authFetchPOST(env.scope, url, payload);
            
            if (!response || !response.id) {
                throw new Error("Failed to update plan with new ETA");
            }
            console.log("Plan updated successfully with new ETA, ID:", response.id);
            return response;
        } catch (error) {
            console.error("Error updating plan with new ETA ", error);
            return null;
        }
    }

    static async getplanInfosByScheduleIds(scheduleIds: string[]): Promise<{ [key: string]: boolean } | null>
    {
        try {
            const res = await authFetchPOST(env.scope, `${env.baseurl}/planinfos`, scheduleIds );
            if(res) {
                return res;
            }
            return null;
        } catch (e) {
            console.error("Error fetching plan infos:", e);
            return null;
        }
    }
}